import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SectionCard } from "../../components/card";
import { PaymentMethod } from "../../components/payment-method";
import { Separator } from "../../components/separator";
import StageCFees from "./stage-c-fees";
import { P2Alert, P2Button, P2ButtonGroup, P2Checkbox, P2Modal, P2TextDescription } from "react-bootstrap-common-script";
import { useNavigate } from "react-router-dom";
import { PolymerAxios } from "../../utils/axios";
import { Helpers } from "../../utils/helper";
import { API_ApplicationCreditNotification, API_ApplicationPayment, API_ApplicationProcessPayment,API_EligibilityDebugLog, API_GetLoanStatus, API_OverrideEligibilityCheck, API_PaymentSuccess, API_PaymentStatus, API_PaymentCheckUpdateStatus } from "../../utils/api";
import { useAppDispatch, useAppSelector } from "../../redux/hook";
import { StampDutyValuationEnum } from "../../models/enum/stamp-duty-valuation.enum";
import { HDBPayableFeeValuation, shouldIncludeValuationFee } from "../../utils/form-helper";
import useCheckRemission from "../../hooks/use-check-remission";
import { ProcessPayment } from "../../models/classes/stage-c/Process-Payment.class";
import { PaymentMethodEnum } from "../../models/enum/payment-method.enum";
import moment from "moment";
import { LottieLoader } from "../../components/loader";
import config from "../../config";
import useGetEligibilityOverride from "../../hooks/use-get-eligibility-override";
import { EligibilityCheck, EligibilityOverride } from "../../models/classes/EligibilityOverride.class";
import { updateFormData } from "../../redux/slices/application-start-slice";
import { ApplicationStart } from "../../models/classes/ApplicationStart.class";
import { Properties, PropertyFinancingInfo } from "../../models/polymer/Properties.model";
import { useWogaa } from "../../hooks/use-wogaa";
import { stageCTrackingId } from "../../utils/wogaa-ids";
import { useGetPaymentCheckUpdateStatusApiQuery } from "../../redux/services/getPaymentCheckUpdateStatus";
import { useGetPaymentStatusApiQuery } from "../../redux/services/getPaymentStatus";
import { useGetTopUpGrantStatusQuery } from "../../redux/services/getTopUpGrant";

const StageCMainColumn = () => {
   
  const AppDispatch = useAppDispatch();
  const queryParameters = new URLSearchParams(window.location.search)
  const isDebug = queryParameters.get("debug");
  const application_id = queryParameters.get("application_id");
  const application_uuid = useAppSelector(state => state.app.ApplicationIdSlice.applicationId);
  const [termsCheckBox, setTermsCheckBox] = useState(false);
  const [isPaymentLoading, setPaymentLoading] = useState<boolean>(false);
  const [paymentMethod, setPaymentMethod] = useState<string>(PaymentMethodEnum.card);
  const [continueEnabled, setContinueEnabled] = useState(false);
  const [totalPaymentAmount, setTotalPaymentAmount] = useState<number>(0);
  const [valuationFee, setValuationFee] = useState<number>(0);
  const [paymentInProgress, setPaymentInProgress] = useState(false);
  const startApplicationFormData = useAppSelector(state => state.app.ApplicationStartSlice.formData);
  const loginUser = useAppSelector((state) => state.auth.AuthenticationSlice.loginUser);
  const singpassToken = useAppSelector(state => state.auth.AuthenticationSlice.singpassToken);
  const hdbRef = startApplicationFormData?.selected_hdb_property?.hdb_ref;
  const applicationStep1FormData = useAppSelector(state => state.app.ApplicationStep1Slice.formData);
  const applicationStep2FormData = useAppSelector(state => state.app.ApplicationStep2Slice.formData);
  const applicationStep3FormData = useAppSelector(state => state.app.ApplicationStep3Slice.formData);
  const { isRemission } = useCheckRemission(
    startApplicationFormData,
    applicationStep1FormData,
    applicationStep2FormData,
    applicationStep3FormData
  );
  const salesTypeCode = startApplicationFormData?.selected_hdb_property?.financing_info?.sales_type_code;
  const [isLoading, setIsLoading] = useState<boolean>(salesTypeCode === undefined);

  const [paymentCompleteModalOpen, setPaymentCompleteModalOpen] = useState(false);
  const [paymentCompleteMessage, setPaymentCompleteMessage] = useState("");
  const [paymentInProgressModalOpen, setPaymentInProgressModalOpen] = useState(false);
  const [paymentStatusModalOpen, setPaymentStatusModalOpen] = useState(false);
  const [paymentStatusMessage, setPaymentStatusMessage] = useState("");
  const [isPaymentDisabled, setIsPaymentDisabled] = useState(false);
  const [statusCheckInterval, setStatusCheckInterval] = useState<NodeJS.Timeout | null>(null);

  const { bypassPayments } = useGetEligibilityOverride(startApplicationFormData.selected_hdb_property?.hdb_ref);
  const [ hasTUGEligibilityModal, setHasTUGEligibilityModal ] = useState<boolean>(false);
  const {
    data: topupGrantData,
    isSuccess: isTopupGrantSuccess,
    isFetching: isTopupGrantFetching,
  } = useGetTopUpGrantStatusQuery(
    { hdb_ref: hdbRef!, uinfin: loginUser?.uinfin ?? '', singpass_token: singpassToken ?? '' },
    { skip: !hdbRef || !loginUser?.uinfin || !singpassToken }
  );
  
  const isTopupGrantRequired = useMemo(() => {
    if (!isTopupGrantSuccess) return false; // error => allow through
    const list = topupGrantData?.topup_grant_case_list ?? [];
    return list.some((e) => e.topup_grant_required);
  }, [isTopupGrantSuccess, topupGrantData]);
  
  const paymentFormRef = useRef<HTMLFormElement>(null); 

  const isProd = useMemo(()=>{
    console.log("process.env.NODE_ENV",process.env.NODE_ENV);
    if (process.env.NODE_ENV !== "production" || document.location.origin === "https://services3-dev.hdb.gov.sg" || document.location.origin === "https://services3-stg.hdb.gov.sg") {
      return false;
    }
    else{
      return true;
    }
  },[])

  let navigate = useNavigate();
  const handlePaymentHandler = useCallback((value: string) => {
    setPaymentMethod(value);
    updateContinueButton(value, termsCheckBox);
  },[termsCheckBox, isPaymentDisabled]);
  
  const { data: paymentCheckUpdateStatusData, isError: isPaymentCheckUpdateStatusError, error: paymentCheckUpdateStatusError, refetch: refetchPaymentCheckUpdateStatus } = useGetPaymentCheckUpdateStatusApiQuery({application_uuid: application_uuid ?? "", singpass_token: singpassToken ?? "" },{skip: !application_uuid})
  const { data: paymentStatusData, isError: isPaymentStatusError, error: paymentStatusError, refetch: refetchPaymentStatus } = useGetPaymentStatusApiQuery({application_uuid: application_uuid ?? "", singpass_token: singpassToken ?? "" },{skip: !application_uuid})
  


  const handleTermsCheckboxToggle = useCallback(() => {
    setTermsCheckBox((value) => !value);
    checkPaymentStatus();
    updateContinueButton(paymentMethod, !termsCheckBox);
  },[paymentMethod, termsCheckBox, isPaymentDisabled]);

  const updateContinueButton = (paymentMethod: string, termsChecked: boolean) => {
    setContinueEnabled(!!paymentMethod && termsChecked && !isPaymentDisabled);
  };

  const isToIncludeValuation = useMemo(() => {
    return shouldIncludeValuationFee({
      isRemission,
      valuation: applicationStep3FormData.valuation,
      newLoanType: applicationStep2FormData.new_loan_type,
      salesTypeCode: startApplicationFormData.selected_hdb_property?.financing_info.sales_type_code,
    });
  }, [
    isRemission,
    applicationStep2FormData.new_loan_type,
    applicationStep3FormData.valuation,
    startApplicationFormData.selected_hdb_property?.financing_info.sales_type_code,
  ]);

  // WOGAA tracking for Stage C - manual start on payment initiation (completion in success/unsuccessful pages)
  const { startTransaction: startStageCWogaa } = useWogaa({
    trackingId: stageCTrackingId,
    autoStart: false,
    autoComplete: false,
    applicationId: application_uuid ?? undefined,
    hdbRef: startApplicationFormData.selected_hdb_property?.hdb_ref,
  });
  
  useEffect(() => {
    if(!application_id || application_id == "undefined" || application_id !== application_uuid){
      window.location.href = "/transfer/dashboard";
    }
  }, [application_id])

  useEffect(() => {
    if(salesTypeCode === undefined && startApplicationFormData?.selected_hdb_property?.hdb_ref) {
      PolymerAxios(loginUser?.uinfin ?? "", singpassToken).post(API_GetLoanStatus, 
        {
          hdb_ref: startApplicationFormData?.selected_hdb_property?.hdb_ref,
        }).then((data) => {
          const updatedStartApplicationForm = new ApplicationStart({...startApplicationFormData, selected_hdb_property: new Properties({...startApplicationFormData.selected_hdb_property, financing_info: new PropertyFinancingInfo({...startApplicationFormData.selected_hdb_property?.financing_info, sales_type_code: data.data.sales_type_code})}) });
          AppDispatch(updateFormData(updatedStartApplicationForm));
          setIsLoading(false);
        }).catch((error) => {
          // Log error to backend before showing alert
          Helpers.logErrorToBackend("StageCPayment_GetLoanStatus_Error", { 
            error: error?.message || error,
            component: "stage-c-payment-main-column.tsx",
            function: "useEffect_getLoanStatus",
            hdbRef: startApplicationFormData?.selected_hdb_property?.hdb_ref,
            applicationId: application_id
          }, loginUser?.uinfin, singpassToken);
          
          console.error("Error fetching loan status:", error);
          alert("An error occurred. Please try again later.");
          window.location.href = "/transfer/dashboard";
        })
    }
  }, [salesTypeCode, startApplicationFormData])

  useEffect(() => {
    if(!startApplicationFormData?.selected_hdb_property?.hdb_ref) {
      window.location.href = "/transfer/dashboard";
    }
  }, [startApplicationFormData])

  // Check payment status when page loads
  useEffect(() => {
    if (application_uuid) {
      checkPaymentStatus();
    }
  }, [application_uuid]);

  // Cleanup interval on component unmount
  useEffect(() => {
    return () => {
      if (statusCheckInterval) {
        clearInterval(statusCheckInterval);
      }
    };
  }, [statusCheckInterval]);

  // Update continue button when payment disabled state changes
  useEffect(() => {
    updateContinueButton(paymentMethod, termsCheckBox);
  }, [isPaymentDisabled, paymentMethod, termsCheckBox]);

  const performByPassPayment = useCallback( async (paymentResponse:any) => {
    const creditNotificationBody: CreditNotificationBody = {
      systIdforPayment: "AC89598",
      numTrnscn: 0,
      totalAmt: 0,
      ttcNum: paymentResponse?.data?.ttcNum, // must euqal with paymentRequest ttcNum
      hashNum: "HASH123",
      transactionDate: moment().format('yyyy-MM-DD'),
      transactionTime: moment().format('HH:mm:ss'),
      details: []
    };

    if (!bypassPayments.admin) {
      creditNotificationBody.numTrnscn += 2;
      creditNotificationBody.totalAmt += 50;
      creditNotificationBody.details = creditNotificationBody.details.concat([
        {
          bankReferenceNo: "REF123",
          receiptNum: "REC123",
          caseNum: "141104S0235",
          systID: "SYS123",
          amount: 45.87,
          rcptngCode: "0875C"
        },
        {
          bankReferenceNo: "REF123",
          receiptNum: "REC123",
          caseNum: "141104S0235",
          systID: "SYS123",
          amount: 4.13,
          rcptngCode: "6225B"
        }
      ])
    }

    if (!bypassPayments.valuation && isToIncludeValuation) {
      creditNotificationBody.numTrnscn += 4;
      creditNotificationBody.totalAmt += 248.90;
      creditNotificationBody.details = creditNotificationBody.details.concat([
        {
          bankReferenceNo: "REF123",
          receiptNum: "REC123",
          caseNum: "141104S0235",
          systID: "SYS123",
          amount: 18.35,
        },
        {
          bankReferenceNo: "REF123",
          receiptNum: "REC123",
          caseNum: "141104S0235",
          systID: "SYS123",
          amount: 1.65,
        },
        {
          bankReferenceNo: "REF123",
          receiptNum: "REC123",
          caseNum: "141104S0235",
          systID: "SYS123",
          amount: 210,
        },
        {
          bankReferenceNo: "REF123",
          receiptNum: "REC123",
          caseNum: "141104S0235",
          systID: "SYS123",
          amount: 18.9,
        }
      ])
    }

    const paymentSuccessRequest = new URLSearchParams();
    paymentSuccessRequest.append("ttcNumSuccess", paymentResponse?.data?.ttcNum ?? "");

    try{
      const creditNotiResponse = await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).post(API_ApplicationCreditNotification, creditNotificationBody);
      const paymentSuccessResponse = await PolymerAxios(loginUser?.uinfin ?? "", singpassToken, "application/x-www-form-urlencoded").post(API_PaymentSuccess, paymentSuccessRequest)

      if(paymentResponse?.data?.status){
        setPaymentLoading(false);
        navigate(`/application-coo/stage-c/payment/successful?application_id=${application_uuid}`)
      }
      else{
        setPaymentLoading(false);
        navigate(`/application-coo/stage-c/payment/unsuccessful?application_id=${application_uuid}`)
      }
    }catch(e){
      setPaymentLoading(false);
    }


  },[application_uuid, isToIncludeValuation, loginUser?.uinfin, navigate, singpassToken, bypassPayments])

  // FIX: Moved setInterval inside useEffect with proper cleanup to prevent memory leak
  // The interval is only created when paymentInProgress is true and properly cleaned up
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    
    if (paymentInProgress) {
      console.log("Starting payment status check interval (5 minutes)");
      intervalId = setInterval(async () => {
        try {
          console.log("Checking payment update status...");
          refetchPaymentCheckUpdateStatus();
        } catch (error) {
          console.error("Error checking payment update status:", error);
        }
      }, 5 * 60 * 1000); // 5 minutes
      
      setStatusCheckInterval(intervalId);
    }
    
    // Cleanup function - properly clears interval when component unmounts or paymentInProgress changes
    return () => {
      if (intervalId) {
        console.log("Clearing payment status check interval");
        clearInterval(intervalId);
      }
    };
  }, [paymentInProgress, refetchPaymentCheckUpdateStatus]);

  const checkPaymentStatus = async () => {
    try {
      refetchPaymentStatus();
    } catch (error) {
      console.error("Error checking payment status:", error);
      if(isPaymentStatusError){
        console.error("Error checking payment status API:", paymentStatusError);
      }
      // Continue normally if API call fails
    }
  };

  // FIX: Removed stale 'interval' reference, now relies on the useEffect above to manage interval
  useEffect(()=>{
    if (paymentStatusData?.payment_status === "SUBMIT_COMPLETE") {
      setPaymentStatusMessage("There is another payment in progress. Please try again later");
      setPaymentStatusModalOpen(true);
      setIsPaymentDisabled(true); 
      setPaymentInProgress(true);
      // Note: interval is now automatically started by the paymentInProgress useEffect above
    } else if (paymentStatusData?.payment_status === "PAYMENT_COMPLETE") {
      setPaymentCompleteMessage("The payment has been successfully completed. Would you like to return to the dashboard?");
      setPaymentCompleteModalOpen(true);
    }
  },[paymentStatusData])

  // FIX: Removed stale 'interval' references, using statusCheckInterval state instead
  useEffect(()=>{
    if (paymentCheckUpdateStatusData?.status === "PAYMENT_COMPLETED") {
      // Clear interval using state variable (will also be cleared by useEffect cleanup)
      if (statusCheckInterval) {
        clearInterval(statusCheckInterval);
        setStatusCheckInterval(null);
      }
      setPaymentStatusModalOpen(false);
      setPaymentCompleteMessage("The payment has been successfully completed. Would you like to return to the dashboard?");
      setPaymentCompleteModalOpen(true);
      setPaymentInProgress(false); // This will trigger interval cleanup via useEffect
    } else if (paymentCheckUpdateStatusData?.status === "PAYMENT_IN_PROGRESS") {
      // Keep showing the dialog
      setPaymentStatusMessage("There is another payment in progress. Please try again later");
      setIsPaymentDisabled(true); 
      setPaymentInProgress(true);
    } else if (paymentCheckUpdateStatusData?.status === "PAYMENT_ERROR_RETRY_AVAILABLE" || paymentCheckUpdateStatusData?.status === "NO_EXISTING_PAYMENT" || paymentCheckUpdateStatusData?.status === "PAYMENT_ERROR") {
      // Clear interval using state variable
      if (statusCheckInterval) {
        clearInterval(statusCheckInterval);
        setStatusCheckInterval(null);
      }
      setPaymentStatusModalOpen(false);
      setIsPaymentDisabled(false);
      setPaymentInProgress(false); // This will trigger interval cleanup via useEffect
    }
  },[paymentCheckUpdateStatusData, statusCheckInterval])
  
  const continuePayment = useCallback(async () => {
    // Start WOGAA Stage C tracking when payment is initiated
    console.log('Start wogaa stage C');
    startStageCWogaa();
    setPaymentLoading(true);
    let processPaymentObj = new ProcessPayment();
    processPaymentObj.setApplicationUUID(application_uuid ?? "");
    processPaymentObj.setPaymentMode(paymentMethod as PaymentMethodEnum);

    const overrideEligibilityCheck = new EligibilityOverride({ application_uuid: application_id });
    
    if (bypassPayments.admin) {
      overrideEligibilityCheck.administrative_fee = EligibilityCheck.BYPASS;
    } else {
      overrideEligibilityCheck.administrative_fee = EligibilityCheck.MUST_CHECK;
      processPaymentObj.addTransactionType("admin");
    }

    if (bypassPayments.valuation) {
      overrideEligibilityCheck.valuation_fee = EligibilityCheck.BYPASS;
    } else if (!isToIncludeValuation) {
      overrideEligibilityCheck.valuation_fee = EligibilityCheck.EXEMPT;
    } else {
      overrideEligibilityCheck.valuation_fee = EligibilityCheck.MUST_CHECK;
      processPaymentObj.addTransactionType("valuation")
    }

    try {
      let processPaymentBody = {
        application_uuid: application_uuid,
      }
      await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).post(API_ApplicationProcessPayment, processPaymentBody);

      await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).post(API_OverrideEligibilityCheck, overrideEligibilityCheck);

      const paymentResponse = await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).post(API_ApplicationPayment, processPaymentObj.json());

      await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).post(API_EligibilityDebugLog, {
        applicationUuid: loginUser?.uinfin,
        message: `FE STAGE C DEBUG LOG CATCH --- Payment url from API : ${paymentResponse?.data?.paymentUrl} | ${paymentResponse?.data?.ttcNum} | ${paymentResponse?.data?.systIdTagPayReq}`
      });

      // Check if payment is already completed
      if (paymentResponse?.data?.status === 200 && 
          paymentResponse?.data?.paymentStatus === "PAYMENT_COMPLETE" && 
          paymentResponse?.data?.message) {
        setPaymentLoading(false);
        setPaymentCompleteMessage(paymentResponse.data.message);
        setPaymentCompleteModalOpen(true);
        return;
      }

      document.getElementById("ttcNumPayReq")?.setAttribute('value', paymentResponse?.data?.ttcNum);
      document.getElementById("systIdTagPayReq")?.setAttribute('value', paymentResponse?.data?.systIdTagPayReq);
      document.getElementById("systIdforPaymentPayReq")?.setAttribute('value', "BR20");
      setPaymentLoading(false);
      // skip payment gateway if the transaction amount if empty
      if (totalPaymentAmount !== 0 && isDebug !== "true") {
        paymentFormRef.current?.submit();
      }

    } catch (error: any) {
      await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).post(API_EligibilityDebugLog, {
        uinfin: loginUser?.uinfin,
        message: `FE STAGE C DEBUG LOG CATCH --- ${error}`
      });

      // Check if it's a payment in progress error
      if (error?.response?.data?.status === 409 && 
          error?.response?.data?.paymentStatus === "PAYMENT_IN_PROGRESS") {
        setPaymentInProgressModalOpen(true);
        setIsPaymentDisabled(true);
        setPaymentInProgress(true);
        // FIX: Removed stale 'interval' reference - interval is now automatically 
        // created by the paymentInProgress useEffect when setPaymentInProgress(true) is called
      } else {
        alert("Oops, something wrong with payment. Please try again.")
      }
      checkPaymentStatus();

      setPaymentLoading(false);
    }

  },[application_uuid, paymentMethod, isToIncludeValuation, loginUser?.uinfin, singpassToken, isDebug, bypassPayments, totalPaymentAmount, startStageCWogaa])

  const handlePaymentCompleteConfirm = useCallback(() => {
    setPaymentCompleteModalOpen(false);
    window.location.href = "/transfer/dashboard";
  }, []);

  const handlePaymentCompleteCancel = useCallback(() => {
    setPaymentCompleteModalOpen(false);
  }, []);

  const handlePaymentInProgressClose = useCallback(() => {
    setPaymentInProgressModalOpen(false);
  }, []);

  const handlePaymentStatusClose = useCallback(() => {
    setPaymentStatusModalOpen(false);
    // If there's an active interval, clear it and reset payment disabled state
    if (statusCheckInterval) {
      clearInterval(statusCheckInterval);
      setStatusCheckInterval(null);
      setIsPaymentDisabled(false);
    }
  }, [statusCheckInterval]);

  const byPassPayment = useCallback(async()=>{
    setPaymentLoading(true);
    let processPaymentObj = new ProcessPayment();
    processPaymentObj.setApplicationUUID(application_uuid ?? "");
    processPaymentObj.setPaymentMode(paymentMethod as PaymentMethodEnum);
    
    const overrideEligibilityCheck = new EligibilityOverride({ application_uuid: application_id });
    
    if (bypassPayments.admin) {
      overrideEligibilityCheck.administrative_fee = EligibilityCheck.BYPASS;
    } else {
      overrideEligibilityCheck.administrative_fee = EligibilityCheck.MUST_CHECK;
      processPaymentObj.addTransactionType("admin");
    }

    if (bypassPayments.valuation) {
      overrideEligibilityCheck.valuation_fee = EligibilityCheck.BYPASS;
    } else if (!isToIncludeValuation) {
      overrideEligibilityCheck.valuation_fee = EligibilityCheck.EXEMPT;
    } else {
      overrideEligibilityCheck.valuation_fee = EligibilityCheck.MUST_CHECK;
      processPaymentObj.addTransactionType("valuation")
    }

    try {
      let processPaymentBody = {
        application_uuid: application_uuid,
      }

      await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).post(API_ApplicationProcessPayment, processPaymentBody);

      await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).post(API_OverrideEligibilityCheck, overrideEligibilityCheck);

      const paymentResponse = await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).post(API_ApplicationPayment, processPaymentObj.json());

      performByPassPayment(paymentResponse);
    } catch (error) {
      console.log(error)
      alert("Oops, something wrong with bypassing. Please try again.")
      setPaymentLoading(false);
    }
  },[application_uuid, isToIncludeValuation, loginUser?.uinfin, paymentMethod, performByPassPayment, singpassToken])

  const navigateBack = useCallback(()=>{
    navigate(-1)
  },[navigate])

  useEffect(() => {
    updateContinueButton(paymentMethod, termsCheckBox);

  }, [paymentMethod, termsCheckBox]);

  useEffect(() => {
      let totalAmount: number = 0;
      if(!bypassPayments.admin){
        totalAmount += 50;
      }

      if (isToIncludeValuation){
        const totalValuationFee = Number(HDBPayableFeeValuation(startApplicationFormData?.selected_hdb_property?.flat_type, applicationStep3FormData?.valuation as StampDutyValuationEnum || undefined));
        setValuationFee(totalValuationFee);

        if (!bypassPayments.valuation) totalAmount += totalValuationFee;
      }

      setTotalPaymentAmount(totalAmount);
  }, [applicationStep3FormData, isToIncludeValuation, startApplicationFormData, bypassPayments])

  const paymentUrl = useMemo(() => {    
    if (document.location.origin === "https://services3.hdb.gov.sg") {
      return config.prodPaymentURL;
    }
    else{
      return config.devAndStgPaymentURL;
    }
  },[])

  const closeTUGEligibilityModal = useCallback(() => {
    setHasTUGEligibilityModal(false);
  }, []);

  const handleContinuePayment = useCallback(async () => {
    // Check TUG api first
    if (isTopupGrantRequired) {
      setHasTUGEligibilityModal(true);
      return;
    } else {
      continuePayment();
    }
  }, [isTopupGrantRequired, continuePayment]);

  return (
    <>
      <form ref={paymentFormRef} method="POST" action={paymentUrl}>
        <input type={isDebug === "true" ? "" :"hidden"} name="ttcNumPayReq" id="ttcNumPayReq" value={""}/>
        <input type={isDebug === "true" ? "" :"hidden"} name="systIdforPaymentPayReq" id="systIdforPaymentPayReq" value={"BR20"}/>
        <input type={isDebug === "true" ? "" :"hidden"} name="systIdTagPayReq" id="systIdTagPayReq" value={""}/>
        {isDebug === "true" && <input type="submit"/>}
      </form>
      <div className='polymer-shared-alert-container'>
          {paymentInProgress &&
          <div style={{ marginTop: 24, marginBottom: 24 }}>
            <P2Alert variant={"danger"} text={"There is another payment that is in progress, please try again later."}></P2Alert>
          </div>}
        </div>
      <div className="d-flex justify-content-center">
        <SectionCard
          required={false}
          className={`flex-fill ${ paymentInProgress ? "polymer-payment-section-with-alert" : "polymer-payment-section"}`}
          titleChildren={<P2TextDescription title={"Payment"} />}
          contentChildren={
            isLoading ? 
              <div>
                <LottieLoader />
              </div> :
              <div className="polymer-main-column-container">
              <p className="polymer-main-column-header">Please note the following fees required for HDB to process your application.</p>
              <StageCFees valuationFee={valuationFee} totalFee={totalPaymentAmount} bypassAdminFee={bypassPayments.admin} bypassValuationFee={bypassPayments.valuation}/>
              <PaymentMethod PaymentHandler={handlePaymentHandler} selectedPaymentMethod={paymentMethod} />
              <div className="polymer-main-column-spacer">
                <Separator />
              </div>
              <div className="polymer-main-column-tnc">
                <p className="polymer-main-column-tnc-content">Please note that all fees paid are non-refundable in the event of any cancellation or rejection of the application. This includes the cancellation of the application should submission of information or documents are not provided within the specified time frame which deem the application as incomplete, or incorrect. For updates on the application progress, you may check our progress tracker once the application is submitted successfully.</p>
                <P2Checkbox
                  isActive={termsCheckBox}
                  onClick={handleTermsCheckboxToggle}
                  description={<p className='m-0 polymer-main-column-tnc-checkbox'>I have read and understood the above and agreed to the <a className="polymer-text-links" href={`${process.env.PUBLIC_URL}/documents/Terms & Conditions for Ownership(Apr 2026).pdf`}>Terms & Conditions for the change in Flat Ownership</a>.</p>}
                />
              </div>
            </div>
          }
        />
      </div>
      
      <Separator style={{margin:"32px 0"}}/>
      <div className='polymer-application-coo__card-button'>
        <P2ButtonGroup>
          <P2Button variant={'outline-primary'} onClick={navigateBack}>
            Cancel
          </P2Button>
          <P2Button variant={'primary-1'} onClick={handleContinuePayment} disabled={!continueEnabled || isTopupGrantFetching}>
            Continue
          </P2Button>
          {!isProd && (
            <P2Button variant={'primary-1'} onClick={byPassPayment} disabled={!continueEnabled || isTopupGrantFetching}>
              ByPass Payment
            </P2Button>
          )}
        </P2ButtonGroup>
      </div>
      <P2Modal
        isOpen={isPaymentLoading}
        children={
          <LottieLoader/>
        }
      />
      
      <P2Modal
         isOpen={paymentCompleteModalOpen}
         onCloseModal={handlePaymentCompleteCancel}
         variant="default"
         title="Payment Completed"
         primaryButtonText="OK"
         primaryButtonFunction={handlePaymentCompleteConfirm}
         secondaryButtonText="Cancel"
         secondaryButtonFunction={handlePaymentCompleteCancel}
         children={
           <p style={{marginBottom: "32px"}}>
             {paymentCompleteMessage}
           </p>
         }
       />
       
       <P2Modal
         isOpen={paymentInProgressModalOpen}
         onCloseModal={handlePaymentInProgressClose}
         variant="default"
         title="Payment In Progress"
         primaryButtonText="OK"
         primaryButtonFunction={handlePaymentInProgressClose}
         children={
           <p style={{marginBottom: "32px"}}>
             There is another payment in progress. Please try again later.
           </p>
         }
       />
       
       <P2Modal
         isOpen={paymentStatusModalOpen}
         onCloseModal={handlePaymentStatusClose}
         variant="default"
         title="Payment Status"
         primaryButtonText="OK"
         primaryButtonFunction={handlePaymentStatusClose}
         children={
           <p style={{marginBottom: "32px"}}>
             {paymentStatusMessage}
           </p>
         }
       />

      <P2Modal
        isOpen={hasTUGEligibilityModal}
        // onCloseModal={closeHasInitiatedApplicationModal}
        variant="default"
        title={`You cannot make payment`}
        primaryButtonText="Back"
        primaryButtonFunction={closeTUGEligibilityModal}
        children={<p>There is an ongoing top-up grant application. Please complete the top-up application process before submitting a change in flat ownership application. Thank you.</p>}
      />
    </>
  );
};

export default StageCMainColumn;

type CreditNotificationBody = {
  systIdforPayment: string;
    numTrnscn: number;
    totalAmt: number;
    ttcNum: any;
    hashNum: string;
    transactionDate: string;
    transactionTime: string;
    details: {
      bankReferenceNo: string;
      receiptNum: string;
      caseNum: string;
      systID: string;
      amount: number;
      rcptngCode?: string;
    }[];
}
